import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  EyeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "../utils/toast";
import { bursariesApi } from "../api/hliLists";
import { errorMessageOf } from "../api/scholarships";
import { downloadExcel } from "../utils/excel";
import { getStatusConfig } from "../config/statusConfig";
import { claimAcademicYear, claimDisplayStatus, claimTypeLabel } from "../config/claimStatus";
import { Button, ConfirmModal, Container, PageHeader, RowAction, Select, StatusBadge, StatusTabs, Table } from "./ui";
import ClaimDetailModal from "./claims/ClaimDetailModal";
import { BURSARIES_KEY, nameOf, useYearOptions } from "./bursaries/shared";

const PAGE_SIZE = 10;
// The most the API returns in one page, for the export
const EXPORT_PAGE_SIZE = 100;
const CLAIMS_KEY = [BURSARIES_KEY, "result-claims"];

const STATUS_FILTERS = [
  { value: "", label: "All claims", color: "#94A3B8" },
  { value: "RECEIVED", label: "Pending", color: getStatusConfig("PENDING").color },
  { value: "APPROVED" },
  { value: "REJECTED" },
];

// Wording of the confirmation for each target status
const STATUS_ACTIONS = {
  APPROVED: { title: "Approve claim?", confirm: "Yes, approve", done: "approved", text: "approve the claim", destructive: false },
  REJECTED: { title: "Reject claim?", confirm: "Yes, reject", done: "rejected", text: "reject the claim", destructive: true },
  RECEIVED: { title: "Revert claim?", confirm: "Yes, revert", done: "reverted to pending", text: "mark the claim back to pending", destructive: false },
};

// Server params shared by the page query and the export
const filterParams = (status, academicYear) => ({
  ...(status && { status }),
  ...(academicYear && { academicYearId: academicYear }),
});

// Admins review the claims students file against their results, then approve, reject or revert them
export default function StudentClaimsList() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);
  const [exporting, setExporting] = useState(false);

  const years = useYearOptions();

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: [...CLAIMS_KEY, page, status, academicYear],
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    queryFn: async () => {
      try {
        return (await bursariesApi.resultClaims({ page, size: PAGE_SIZE, ...filterParams(status, academicYear) })).data;
      } catch (error) {
        console.error("Failed to load student claims:", error);
        toast.error(errorMessageOf(error, "Failed to load claims"), { id: "student-claims-error" });
        throw error;
      }
    },
  });

  const totalElements = data?.total ?? 0;
  const rows = (data?.items ?? []).map((c, i) => ({ ...c, index: page * PAGE_SIZE + i + 1 }));

  const updateStatus = useMutation({
    mutationFn: ({ claim, newStatus }) => bursariesApi.setResultClaimStatus(claim.id, newStatus),
    onSuccess: ({ data: saved }, { newStatus }) => {
      toast.success(`Claim ${STATUS_ACTIONS[newStatus].done} successfully`, { autoClose: 3000 });
      setSelectedClaim((open) => (open?.id === saved.id ? saved : open));
      queryClient.invalidateQueries({ queryKey: CLAIMS_KEY });
    },
    onError: (error) => {
      console.error("Failed to update the claim status:", error);
      toast.error(errorMessageOf(error, "Failed to update claim status. Please try again."), { autoClose: 4000 });
    },
    onSettled: () => setPendingChange(null),
  });
  const updatingId = updateStatus.isPending ? updateStatus.variables?.claim.id : null;

  const changeFilter = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      const all = [];
      for (let p = 0; p * EXPORT_PAGE_SIZE < totalElements; p++) {
        const { data: found } = await bursariesApi.resultClaims({ page: p, size: EXPORT_PAGE_SIZE, ...filterParams(status, academicYear) });
        all.push(...found.items);
      }
      const exportRows = all.map((c, i) => ({
        "#": i + 1,
        "Reg Number": c.registrationNumber || "",
        Student: nameOf(c),
        Institution: c.institution?.name || "",
        "Claim Type": claimTypeLabel(c.claimType),
        "Claim Description": c.description || "",
        "Academic Year": claimAcademicYear(c),
        Status: getStatusConfig(claimDisplayStatus(c.status)).label,
        "Decided By": c.decidedBy?.name || "",
      }));
      const yearName = years.find((y) => y.value === academicYear)?.label;
      downloadExcel(exportRows, ["Student_Claims", yearName, status].filter(Boolean).join("_"), "Student Claims");
      toast.success("Excel file downloaded successfully.", { autoClose: 3000 });
    } catch (error) {
      console.error("Failed to export claims:", error);
      toast.error("Failed to export claims. Please try again.", { autoClose: 4000 });
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    { key: "index", label: "#", className: "text-slate-500" },
    { key: "registrationNumber", label: "Reg number", className: "font-semibold text-slate-900" },
    { key: "student", label: "Student", accessor: nameOf },
    { key: "institution", label: "Institution", accessor: (c) => c.institution?.name ?? "—" },
    { key: "claimType", label: "Claim type", accessor: (c) => claimTypeLabel(c.claimType) },
    { key: "description", label: "Description", render: (c) => <span className="line-clamp-2 max-w-xs">{c.description || "—"}</span> },
    { key: "academicYear", label: "Academic year", accessor: claimAcademicYear, className: "whitespace-nowrap" },
    {
      key: "status",
      label: "Status",
      accessor: (c) => getStatusConfig(claimDisplayStatus(c.status)).label,
      render: (c) => <StatusBadge status={claimDisplayStatus(c.status)} />,
    },
    {
      key: "actions",
      label: "",
      exportable: false,
      render: (c) => {
        const busy = updatingId === c.id;
        const change = (newStatus) => setPendingChange({ claim: c, newStatus });
        return (
          <div className="flex justify-end gap-1">
            <RowAction label="View details" Icon={EyeIcon} onClick={() => setSelectedClaim(c)} />
            {c.status === "RECEIVED" ? (
              <>
                <RowAction label="Approve" Icon={CheckIcon} variant="soft" loading={busy} onClick={() => change("APPROVED")} />
                <RowAction label="Reject" Icon={XMarkIcon} variant="danger-soft" loading={busy} onClick={() => change("REJECTED")} />
              </>
            ) : (
              <RowAction label="Revert to pending" Icon={ArrowUturnLeftIcon} loading={busy} onClick={() => change("RECEIVED")} />
            )}
          </div>
        );
      },
    },
  ];

  const action = pendingChange && STATUS_ACTIONS[pendingChange.newStatus];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Student Claims"
        subtitle="Review the claims students file against their results"
        actions={
          <Button variant="ghost" icon={<ArrowPathIcon className="h-4 w-4" />} onClick={() => refetch()} loading={isFetching && !isPending}>
            Refresh
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <StatusTabs
          tabs={STATUS_FILTERS.map((t) => (t.value === status ? { ...t, count: totalElements } : t))}
          value={status}
          onChange={changeFilter(setStatus)}
        />
        <Table cardView viewKey="student-claims"
          columns={columns}
          data={rows}
          isLoading={isPending}
          onRowClick={setSelectedClaim}
          searchPlaceholder="Search this page"
          exportable={false}
          emptyTitle="No claims found"
          filters={
            <Select
              aria-label="Academic year"
              value={academicYear}
              onChange={(e) => changeFilter(setAcademicYear)(e.target.value)}
              options={[{ value: "", label: "All years" }, ...years]}
              className="w-full sm:w-48"
            />
          }
          actions={
            <Button variant="outline" icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={exportAll} loading={exporting} disabled={!totalElements}>
              Export Excel
            </Button>
          }
          pagination={{ page, totalPages: Math.ceil(totalElements / PAGE_SIZE), totalElements, pageSize: PAGE_SIZE, onPageChange: setPage }}
        />
      </div>

      <ClaimDetailModal
        claim={selectedClaim}
        updating={Boolean(selectedClaim) && updatingId === selectedClaim.id}
        onClose={() => setSelectedClaim(null)}
        onChangeStatus={(claim, newStatus) => setPendingChange({ claim, newStatus })}
      />
      <ConfirmModal
        open={Boolean(pendingChange)}
        onClose={() => setPendingChange(null)}
        onConfirm={() => updateStatus.mutate(pendingChange)}
        loading={updateStatus.isPending}
        destructive={action?.destructive}
        title={action?.title}
        confirmLabel={action?.confirm}
        description={pendingChange && `You are about to ${action.text} for student ${pendingChange.claim.registrationNumber}. This can be reverted.`}
      />
    </Container>
  );
}
